import React, { memo, useMemo, ReactNode } from 'react';
import T from 'i18n-react';
import Slider, { Settings } from 'react-slick';
import { makeStyles, Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';

import GoImage from '../../../../../images/home/slide_go_lg.jpg';
import GoImageSm from '../../../../../images/home/slide_go_sm.jpg';
import HowtoImageLg from '../../../../../images/home/slide_howto_lg.jpg';
import HowtoImageSm from '../../../../../images/home/slide_howto_sm.jpg';
import HotSpring2022Lg from '../../../../../images/home/slide_hot_spring_2022_lg.jpg';
import HotSpring2022Sm from '../../../../../images/home/slide_hot_spring_2022_sm.jpg';
import AnyAge2022Lg from '../../../../../images/home/slide_any_age_2022_lg.jpg';
import AnyAge2022Sm from '../../../../../images/home/slide_any_age_2022_sm.jpg';
import Language2025Lg from '../../../../../images/home/language_2025_lg.jpg';
import Language2025Sm from '../../../../../images/home/language_2025_sm.jpg';
import Ninki2025Lg from '../../../../../images/home/slide_ninki_lg.png';
import Ninki2025Sm from '../../../../../images/home/slide_ninki_sm.png';
import Primary2025Lg from '../../../../../images/home/slide_primary_2025_lg.jpg';
import Primary2025Sm from '../../../../../images/home/slide_primary_2025_sm.jpg';
import NearSea2026Lg from '../../../../../images/home/slide_near_sea_2026_lg.jpg';
import NearSea2026Sm from '../../../../../images/home/slide_near_sea_2026_sm.jpg';
import Hishochi2026Lg from '../../../../../images/home/slide_hishochi_2026_lg.jpg';
import Hishochi2026Sm from '../../../../../images/home/slide_hishochi_2026_sm.jpg';
import Yakai2026Lg from '../../../../../images/home/slide_yakai_2026_lg.jpg';
import Yakai2026Sm from '../../../../../images/home/slide_yakai_2026_sm.jpg';
import Hiruobi2026Lg from '../../../../../images/home/slide_hiruobi_2026_lg.jpg';
import Hiruobi2026Sm from '../../../../../images/home/slide_hiruobi_2026_sm.jpg';
import SummerBreak2026Lg from '../../../../../images/home/slide_summer_break_2026_lg.jpg';
import SummerBreak2026Sm from '../../../../../images/home/slide_summer_break_2026_sm.jpg';
import TwoWeekPrivateRoom2026Lg from '../../../../../images/home/slide_2week_private_room_lg.jpg';
import TwoWeekPrivateRoom2026Sm from '../../../../../images/home/slide_2week_private_room_sm.jpg';

import { ABOUT } from '../../../routing';
import AppAvatar from '../../../../core/Avatar/AppAvatar';
import {
  GO_BANNER,
  ABOUT_BUTTON,
  HOWTO_MV,
  HOT_SPRING_2022_MV,
  ANY_AGE_2022_MV,
  LANGUAGE_2025_MV,
  NINKI_2025_MV,
  PRIMARY_2025_MV,
  NEAR_SEA_2026_MV,
  HISHOCHI_2026_MV,
  YAKAI_2026_MV,
  HIRUOBI_2026_MV,
  SUMMER_BREAK_2026_MV,
  TWO_WEEK_PRIVATE_ROOM_2026_MV,
} from '../../../constants/gtm';

const SLIDER_SETTINGS: Settings = {
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  speed: 600,
  autoplaySpeed: 3600,
  centerMode: true,
  arrows: false,
  dots: false,
  centerPadding: '25px',
};

const useStyles = makeStyles((theme) => ({
  slider: {
    margin: theme.spacing(2, 0, 2),
    '& .slick-track': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .slick-dots': {
      [theme.breakpoints.up('sm')]: {
        bottom: '6%',
      },
      '& li': {
        width: 15,
        height: 15,
        margin: theme.spacing(0, 1),
        '& button:before': {
          width: 15,
          height: 15,
          border: '1px solid #fff',
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[500],
          content: '""',
          opacity: 1,
        },
        '&.slick-active button:before': {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
        },
        [theme.breakpoints.only('sm')]: {
          margin: '0 3px',
          '& button': {
            width: 15,
            height: 15,
          },
          '& button:before': {
            width: 10,
            height: 10,
          },
        },
      },
    },
  },
  item: {
    position: 'relative',
    paddingRight: theme.spacing(1.25),
    paddingLeft: theme.spacing(1.25),
  },
  avatar: {
    width: '100%',
    height: 'auto',
    margin: 'auto',
  },
  skeleton: {
    position: 'relative',
    paddingTop: `${(630 / 660) * 100}%`,
    borderRadius: 30,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `${(400 / 1200) * 100}%`,
      borderRadius: 70,
    },
    '& > img': {
      display: 'none',
    },
    '& + a': {
      display: 'none',
    },
  },
  button: {
    position: 'absolute',
    bottom: '5%',
    left: '2%',
    marginLeft: '1%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: 25,
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.pxToRem(14),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12),
      marginLeft: '1.7rem',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
}));

type SliderItemProps = {
  src: string;
  alt: string;
  href: string;
  dataGtm: string;
  target?: string;
  classes: { [key: string]: string };
  button?: boolean;
  aboutButtonDataGtm?: string;
  buttonText?: ReactNode;
};

const SliderItem = memo(
  ({
    href,
    src,
    button = false,
    dataGtm,
    alt,
    classes,
    aboutButtonDataGtm = ABOUT_BUTTON,
    buttonText = T.translate('homes.learnMore'),
    target = '_self',
  }: SliderItemProps) => (
    <div className={classes.item}>
      <AppAvatar
        variant="square"
        src={src}
        alt={alt}
        className={classes.avatar}
        AvatarOnlyProps={{
          component: Link,
          href,
          target: target,
          rel: 'noopener noreferrer',
        }}
        SkeletonProps={{ className: classes.skeleton }}
        data-gtm={dataGtm}
      />
      {button && (
        <Button
          className={classes.button}
          color="primary"
          variant="contained"
          href={href}
          data-gtm={aboutButtonDataGtm}
        >
          {buttonText}
        </Button>
      )}
    </div>
  ),
);

const TopSlider = () => {
  const classes = useStyles();

  const isOverSm = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));

  const items = useMemo<Omit<SliderItemProps, 'classes'>[]>(
    () => [
      {
        src: isOverSm ? TwoWeekPrivateRoom2026Lg : TwoWeekPrivateRoom2026Sm,
        alt: '2週間×1人部屋に泊まれる',
        href: 'https://otetsutabi.com/plans?category=38&endDate=2026-08-31&startDate=2026-07-01&term=14',
        dataGtm: TWO_WEEK_PRIVATE_ROOM_2026_MV,
      },
      {
        src: isOverSm ? SummerBreak2026Lg : SummerBreak2026Sm,
        alt: '夏休み特集',
        href: 'https://otetsutabi.com/theme/summer-break-students',
        dataGtm: SUMMER_BREAK_2026_MV,
      },
      {
        src: isOverSm ? Yakai2026Lg : Yakai2026Sm,
        alt: '櫻井・有吉 THE夜会',
        href: 'https://otetsutabi.com/theme/yakai',
        dataGtm: YAKAI_2026_MV,
      },
      {
        src: isOverSm ? Hiruobi2026Lg : Hiruobi2026Sm,
        alt: 'ひるおび',
        href: 'https://otetsutabi.com/theme/hiruobi',
        dataGtm: HIRUOBI_2026_MV,
      },
      {
        src: isOverSm ? NearSea2026Lg : NearSea2026Sm,
        alt: '海の近く',
        href: '/plans?specific_category_id=40,50&utm_medium=mv&utm_campaign=2026_sea',
        dataGtm: NEAR_SEA_2026_MV,
      },
      {
        src: isOverSm ? Hishochi2026Lg : Hishochi2026Sm,
        alt: '避暑地',
        href: '/plans?region=1,20,9,10,19,22&utm_medium=mv&utm_campaign=2026_hisyochi',
        dataGtm: HISHOCHI_2026_MV,
      },
      {
        src: isOverSm ? Ninki2025Lg : Ninki2025Sm,
        alt: '人気条件特集',
        href: '/plans?category=35%2C38&term=21&overReviewPoints=4.0&utm_source=user+applications&utm_medium=mv&utm_campaign=ninki',
        dataGtm: NINKI_2025_MV,
      },
      {
        src: isOverSm ? AnyAge2022Lg : AnyAge2022Sm,
        alt: '年齢不問',
        href: '/plans?category=26',
        dataGtm: ANY_AGE_2022_MV,
      },
      {
        src: isOverSm ? HotSpring2022Lg : HotSpring2022Sm,
        alt: '温泉特集',
        href: '/plans?category=7',
        dataGtm: HOT_SPRING_2022_MV,
      },
      {
        src: isOverSm ? Primary2025Lg : Primary2025Sm,
        alt: '農業林業特集',
        href: '/plans?type=agri',
        dataGtm: PRIMARY_2025_MV,
      },
      {
        src: isOverSm ? Language2025Lg : Language2025Sm,
        alt: '語学を活かせるおてつたび特集',
        href: '/plans?category=49',
        dataGtm: LANGUAGE_2025_MV,
      },
      {
        src: isOverSm ? HowtoImageLg : HowtoImageSm,
        alt: T.translate('homes.howto') as string,
        href: 'https://media.otetsutabi.com/20240509-2/?utm_source=main+banner&utm_medium=otetsunagi&utm_campaign=user+how+to',
        dataGtm: HOWTO_MV,
      },
      {
        src: isOverSm ? GoImage : GoImageSm,
        alt: T.translate('homes.learnMore') as string,
        href: ABOUT,
        button: true,
        dataGtm: GO_BANNER,
      },
    ],
    [isOverSm],
  );

  return (
    <Slider {...SLIDER_SETTINGS} className={classes.slider}>
      {items.map((props) => (
        <SliderItem {...props} classes={classes} key={props.src} />
      ))}
    </Slider>
  );
};
export default TopSlider;
